/**
 * AI Service API Client
 * Handles communication with waitergpt-botstack AI service
 */

import type { BotRequest, BotResponse } from '../types/agenticChat';

// Direct connection to AI service for minimal latency
const AI_SERVICE_BASE_URL = import.meta.env.VITE_AI_SERVICE_URL || '';

/**
 * Send a message to the AI chatbot (DIRECT connection)
 * NOTE: AI service must have CORS enabled for frontend domain
 * @param request Bot request payload
 * @returns Bot response with session_id, reply, and optional actions
 */
export async function sendChatMessage(request: BotRequest): Promise<BotResponse> {
  const url = AI_SERVICE_BASE_URL ? `${AI_SERVICE_BASE_URL}/bot/chat` : '/bot/chat';
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(
        errorData?.detail || errorData?.error || `AI service error: ${response.status}`
      );
    }

    const data: BotResponse = await response.json();

    // Check for error in response
    if (data.error) {
      throw new Error(data.error);
    }

    return data;
  } catch (error) {
    console.error('[AgenticChatAPI] Error sending message:', error);

    // Re-throw with user-friendly message
    if (error instanceof Error) {
      throw error;
    }

    throw new Error('Failed to communicate with AI service. Please try again.');
  }
}

/**
 * Send initial empty call to get AI greeting
 * @param restaurantId Restaurant ID
 * @param tableId Table ID from QR code
 * @returns Bot response with greeting
 */
export async function initializeChat(
  restaurantId: string,
  tableId: string
): Promise<BotResponse> {
  return sendChatMessage({
    session_id: null,
    restaurant_id: restaurantId,
    table_id: tableId,
    message: '',
    audio_data: null,
    format: null,
  });
}

/**
 * Send a text message in an existing session
 * @param sessionId AI session ID
 * @param restaurantId Restaurant ID
 * @param tableId Table ID
 * @param message Customer message text
 * @returns Bot response
 */
export async function sendMessage(
  sessionId: string,
  restaurantId: string,
  tableId: string,
  message: string
): Promise<BotResponse> {
  return sendChatMessage({
    session_id: sessionId,
    restaurant_id: restaurantId,
    table_id: tableId,
    message,
    audio_data: null,
    format: null,
  });
}

/**
 * Send an audio message in an existing session
 * @param sessionId AI session ID
 * @param restaurantId Restaurant ID
 * @param tableId Table ID
 * @param audioData Base64-encoded audio data
 * @param format Audio format (e.g., 'wav', 'webm', 'mp3')
 * @returns Bot response
 */
export async function sendAudioMessage(
  sessionId: string,
  restaurantId: string,
  tableId: string,
  audioData: string,
  format: string
): Promise<BotResponse> {
  return sendChatMessage({
    session_id: sessionId,
    restaurant_id: restaurantId,
    table_id: tableId,
    message: '',
    audio_data: audioData,
    format: format,
  });
}
